import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Check, Eye, EyeSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_lesson-card-manager/artifacts/zd0x6jcg_c982a405-739a-4717-a6a0-d9774dc13353_20251118_231830_0000.png";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Wachtwoord moet minimaal 6 tekens zijn');
            return;
        }
        if (password !== confirmPassword) {
            setError('Wachtwoorden komen niet overeen');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Er is iets misgegaan. De link is mogelijk verlopen.');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#1A202C] flex items-center justify-center p-4">
                <div className="bg-[#2D3748] p-8 rounded-lg border border-[#4A5568] text-center max-w-md">
                    <h2 className="text-xl font-semibold text-white mb-4">Ongeldige link</h2>
                    <p className="text-gray-400 mb-6">Deze reset link is ongeldig. Vraag een nieuwe aan.</p>
                    <Link to="/forgot-password">
                        <Button className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold">
                            Nieuwe link aanvragen
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#1A202C] flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <img src={LOGO_URL} alt="AutoPilot" className="w-20 h-20 mx-auto mb-4 object-contain" />
                    <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Rijschool <span className="text-[#D4A537]">AutoPilot</span>
                    </h1>
                </div>

                <div className="bg-[#2D3748] p-8 rounded-lg border border-[#4A5568]">
                    {success ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check size={32} className="text-emerald-400" />
                            </div>
                            <h2 className="text-xl font-semibold text-white mb-2">Wachtwoord gewijzigd!</h2>
                            <p className="text-gray-400 mb-6">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
                            <Link to="/login">
                                <Button className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold">
                                    Naar inloggen
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Nieuw wachtwoord
                            </h2>
                            <p className="text-gray-400 mb-6 text-sm">Kies een nieuw wachtwoord voor je account.</p>

                            {error && (
                                <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm">{error}</div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-gray-300">Nieuw wachtwoord</Label>
                                    <div className="relative">
                                        <Input type={showPassword ? 'text' : 'password'} value={password}
                                            onChange={(e) => setPassword(e.target.value)} placeholder="Minimaal 6 tekens" required
                                            className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 pr-10"
                                            data-testid="reset-password-input" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                                            {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-300">Herhaal wachtwoord</Label>
                                    <Input type="password" value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Herhaal je wachtwoord" required
                                        className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                        data-testid="reset-confirm-input" />
                                </div>
                                <Button type="submit" disabled={loading}
                                    className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                    data-testid="reset-submit-button">
                                    <Lock size={18} className="mr-2" />
                                    {loading ? 'Bezig...' : 'Wachtwoord wijzigen'}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
